import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WidgetContainer from "../../UiLib/WidgetContainer/WidgetContainer";
import "./Notifications.css"
import CheckboxIos from "../../UiLib/CheckboxIos/CheckboxIos";
import useOutsideAlerter from "../../../hooks/useOutsideAlert";
import DropDownList from "../../UiLib/DropDownList/DropDownList";
import { Context } from "../../../index";

import { SortType, sortAny } from '../../../helpers/sortUtils';
import { scrollYCheck } from '../../../helpers/domUtils';
import { useSocketContext } from '../../../contexts/SocketContext';
import NotificationBlock from './NotificationBlock';
import { handleError } from '../../../helpers/ErrorLib/handleError';
import { AppContext } from '../../../App';
import { INotification, IRespNotification } from '../../../common/api_interfaces/notifications/IRespNotification';
import { IGetNotifications } from '../../../common/api_interfaces/notifications/IGetNotifications';
import { EntityType } from '../../../common/enums/TypeOfChange';

interface INotifications {
    onClose: () => void;
}

const Notifications: React.FC<INotifications> = ({ onClose }) => {
    const outsideAlerterRef = useOutsideAlerter<HTMLDivElement>(onClose);
    const { notification, changeNotification } = useSocketContext();
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);
    const navigate = useNavigate();

    const [currentNotification, setCurrentNotification] = useState<INotification[]>(notification?.data?.notifications || []);
    const [countNotViewed, setCountNotViewed] = useState<number>(notification?.data?.countNotViewedData || 0);
    const [blockFetch, setBlockFetch] = useState<boolean>(false);
    const [onlyNotViewed, setOnlyNotViewed] = useState<boolean>(false);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const fetchNotifications = useCallback(async (skip: number, viewed: boolean) => {
        const reqBody: IGetNotifications = {
            skip,
            take: 10,
            filters: {
                onlyNotViewed: viewed,
                mention: true
            },
            sort: {
                date: "desc"
            }
        };

        try {
            return await store.notificationService.getNotifications(reqBody);
        } catch (err) {
            console.error(err);
            return undefined;
        }
    }, [store.notificationService]);

    const fetchInitialData = useCallback(async () => {
        const result = await fetchNotifications(0, false);
        return result;
    }, [fetchNotifications]);

    const checkNotificationViewed = useCallback(async (checkIds: number[]) => {
        try {
            await store.notificationService.checkNotificationViewed(checkIds);
            let resp = await fetchInitialData();

            if (!resp?.data?.data) {
                setCurrentNotification([]);
                setBlockFetch(false);
                return;
            }

            const currentGetData = resp.data.data;
            const dateNotifications: INotification[] = currentGetData.notifications || [];
            const notificationCount: number = currentGetData.allCount || 0;

            setCurrentNotification(() => {
                const sorted = sortAny(SortType.date, dateNotifications, "desc", "createdAt");
                return sorted || dateNotifications;
            });
            setBlockFetch(notificationCount <= 0);
            setCountNotViewed(currentGetData.allCount);
            changeNotification(resp.data);
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при загрузке уведомлений",
                showToast: showToast
            });
        }
    }, [store.notificationService, fetchInitialData]);

    useEffect(() => {
        if (!notification?.data) return;

        const newNotifications = notification.data.notifications as INotification[];
        const notificationCount = notification.data.allCount as number;

        setCurrentNotification(prev => {
            const newItems = newNotifications.filter(
                newNotif => !prev.some(existingNotif => existingNotif.id === newNotif.id)
            );

            if (newItems.length === 0) return prev;

            const merged = [...newItems, ...prev];
            return sortAny(SortType.date, merged, "desc", "createdAt") || merged;
        });

        setBlockFetch(notificationCount <= 0);
    }, [notification]);

    const filterUnreadMessages = useCallback(async () => {
        const newViewedState = !onlyNotViewed;
        setOnlyNotViewed(newViewedState);

        const newGetData = await fetchNotifications(0, newViewedState);

        if (!newGetData?.data?.data) {
            setCurrentNotification([]);
            setBlockFetch(false);
            return;
        }

        const currentGetData = newGetData.data.data;
        const dateNotifications: INotification[] = currentGetData.notifications || [];
        const notificationCount: number = currentGetData.allCount || 0;

        setCurrentNotification(() => {
            const sorted = sortAny(SortType.date, dateNotifications, "desc", "createdAt");
            return sorted || dateNotifications;
        });

        setBlockFetch(notificationCount <= 0);
    }, [onlyNotViewed, fetchNotifications]);

    const handleScroll = useCallback(async (event: React.UIEvent<HTMLDivElement>) => {
        if (scrollYCheck(event, 1) && !blockFetch && currentNotification.length > 0) {
            const newGetData = await fetchNotifications(currentNotification.length, onlyNotViewed);

            if (!newGetData?.data?.data) return;

            const currentGetData = newGetData.data.data;
            const newNotifications: INotification[] = currentGetData.notifications || [];
            const notificationCount: number = currentGetData.allCount || 0;

            const uniqueNewNotifications = newNotifications.filter(
                newNotif => !currentNotification.some(existingNotif => existingNotif.id === newNotif.id)
            );

            if (uniqueNewNotifications.length > 0) {
                setCurrentNotification(prev => {
                    const merged = [...uniqueNewNotifications, ...prev];
                    const sorted = sortAny(SortType.date, merged, "desc", "createdAt");
                    return sorted || merged;
                });
            }

            setBlockFetch(notificationCount <= 0);
        }
    }, [blockFetch, currentNotification, onlyNotViewed, fetchNotifications]);

    const goToEntity = useCallback(async (notificationId: number, entityId: number, entityType: EntityType, messageId?: number) => {
        try {
            await checkNotificationViewed([notificationId]);
            onClose();

            const checklistParam = entityType === EntityType.taskComment ? `?checkList=0&tab=0` : `?checkList=1&tab=1`;
            const hash = messageId ? `#message-${messageId}` : '';
            navigate(`/task/${entityId}${checklistParam}${hash}`);

            const newGetData = await fetchInitialData();
            if (!newGetData?.data) return;

            const curentData: IRespNotification = newGetData.data;
            const dateNotifications: INotification[] = curentData.data?.notifications || [];
            const notificationCount: number = curentData.data?.allCount || 0;
            const countNotViewedNot: number = curentData.data?.countNotViewedData || 0;

            changeNotification({
                data: {
                    notifications: dateNotifications,
                    allCount: notificationCount,
                    countNotViewedData: countNotViewedNot
                }
            });

            setCurrentNotification(() => {
                const sorted = sortAny(SortType.date, dateNotifications, "desc", "createdAt");
                return sorted || dateNotifications;
            });

            setCountNotViewed(countNotViewedNot);
            setBlockFetch(notificationCount <= 0);
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при загрузке уведомлений",
                showToast: showToast
            });
        }
    }, [checkNotificationViewed, onClose, navigate, fetchInitialData, changeNotification]);

    const getMentionInText = useCallback((content: string) => {
        const dom = new DOMParser();
        const doc = dom.parseFromString(content, 'text/html');
        const allMentions = doc.querySelectorAll(".mention-custom");

        if (allMentions.length === 0) return null;

        return (
            <div>
                {Array.from(allMentions).map((mention, index) => (
                    <span
                        key={index}
                        dangerouslySetInnerHTML={{ __html: mention.outerHTML }}
                    />
                ))}
            </div>
        );
    }, []);

    const notificationBlocks = useMemo(() => {
        return currentNotification.map((notif) => (
            <NotificationBlock
                key={notif.id}
                notification={notif}
                onCheckViewed={checkNotificationViewed}
                onGoToEntity={goToEntity}
                getMentionInText={getMentionInText}
            />
        ));
    }, [currentNotification, checkNotificationViewed, goToEntity, getMentionInText]);

    return (
        <div ref={outsideAlerterRef}>
            <WidgetContainer style={{ width: "480px", height: '550px', display: 'flex', flexDirection: 'column' }} >
                <div className="notification--block__header">
                    <h3>Уведомления</h3>
                    <p>Только непрочитанные</p>
                    <CheckboxIos
                        id={"checkbox-notifications"}
                        checked={onlyNotViewed}
                        callBack={filterUnreadMessages}
                    />
                </div>
                <div
                    ref={scrollContainerRef}
                    className="notification--block__body"
                    onScroll={handleScroll}
                >
                    <DropDownList
                        numChild={countNotViewed}
                        initialStateOpen={countNotViewed > 0}
                        componentMod="dropDownList"
                        listName={"Упоминания"}
                    >
                        <div>
                            {notificationBlocks}
                        </div>
                    </DropDownList>
                </div>
            </WidgetContainer>
        </div>
    );
};

NotificationBlock.displayName = 'Notifications';

export default Notifications;